import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { ConfigurationsService } from './configurations.service';
import { Tenant } from '../interfaces/tenant';
import { User } from '../interfaces/user';
import { JwtService } from './jwt.service';
import { StorageTypeKey } from '../enums/StorageTypeKey';
import { statusTag } from '../enums/TenantStatusTag';
import { BehaviorSubject } from 'rxjs';
import { JWTPayloadV2User, JWTPayload } from '../interfaces/jwtPayload';
import { DistanceUnit } from '../enums/DistanceUnit';
import { PasswordValidationResponse } from '../interfaces/passwordValidationResult';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  tenantInZeusView: BehaviorSubject<Tenant> = new BehaviorSubject<Tenant>(null);

  constructor(
    private http: HttpClient,
    private configurationsService: ConfigurationsService,
    private jwtService: JwtService,
  ) {}

  public getToken(): string {
    return localStorage.getItem(StorageTypeKey.ACCESS_TOKEN);
  }
  public getRefreshToken(): string {
    return localStorage.getItem(StorageTypeKey.REFRESH_TOKEN);
  }
  public getDecodedToken(): JWTPayload {
    const token = this.getToken();

    if (!token) {
      return {};
    }

    return this.jwtService.decode(token);
  }
  public setRefreshToken(refreshToken: string): void {
    localStorage.setItem(StorageTypeKey.REFRESH_TOKEN, refreshToken);
  }
  public getClientGuid(): string {
    return localStorage.getItem(StorageTypeKey.CLIENT_GUID);
  }
  public setClientGuid(guid: string): void {
    localStorage.setItem(StorageTypeKey.CLIENT_GUID, guid);
  }
  public async isAuthenticated(): Promise<boolean> {
    const token = this.getToken();
    if (token) {
      try {
        return !!this.getUser();
      } catch (error) {
        return false;
      }
    }
    return false;
  }

  public isTenantVerified(): boolean {
    return this.getTenant()?.isVerified;
  }

  public isUsersEmailVerified(): boolean {
    return this.jwtService.decode(this.getToken()).v2.user.isEmailVerified;
  }

  async readToken(): Promise<{ tenant: Tenant; user: User }> {
    return { tenant: this.getTenant(), user: this.getUser() };
  }

  /**
   * @deprecated
   */
  async refreshTokenData(): Promise<void> {
    const { tenant, user } = await this.readToken();
    this.setTenant(tenant);
    this.setUser(user);
  }

  async resendVerificationEmail(): Promise<void> {
    return await this.http
      .post<void>(`${environment.authMSServerUrl}/verify-email/resend`, { clientGuid: this.getClientGuid() })
      .toPromise();
  }

  async validatePasswordStrength(password: string): Promise<PasswordValidationResponse> {
    return await this.http
      .post<any>(`${environment.authMSServerUrl}/validate-password`, { password })
      .toPromise();
  }

  async loginToXero(): Promise<void> {
    const result = await this.http
      .get<{ redirectUrl: string }>(`${environment.serverUrl}/api/auth/third-party/xero/login`)
      .toPromise();
    window.location.href = result.redirectUrl;
  }

  public setToken(token: string) {
    localStorage.setItem(StorageTypeKey.ACCESS_TOKEN, token);
  }

  public getTenant(): Tenant | undefined {
    const payload = this.getDecodedToken();
    return payload?.tenant;
  }

  public getBrokerHaulier() {
    const payload = this.jwtService.decode(this.getToken());
    return payload?.brokerHauliers[0];
  }

  public setTenant(tenant) {
    localStorage.setItem('tenant', JSON.stringify(tenant));
  }

  public getUser(): User | undefined {
    const payload = this.getDecodedToken();
    return payload?.user;
  }

  public getV2User(): JWTPayloadV2User {
    const payload = this.jwtService.decode(this.getToken());
    return payload?.v2.user;
  }

  public setUser(user) {
    localStorage.setItem('user', JSON.stringify(user));
  }
  public setZoomLevel(zoomLevel) {
    localStorage.setItem('zoomLevel', zoomLevel);
  }

  public getZoomLevel() {
    return JSON.parse(localStorage.getItem('zoomLevel'));
  }

  public setJobView(jobsView) {
    localStorage.setItem('jobsView', JSON.stringify(jobsView));
  }

  public getJobView() {
    return JSON.parse(localStorage.getItem('jobsView'));
  }

  public setTimeFormat(timeFormat) {
    localStorage.setItem('timeFormat', timeFormat);
  }

  public setDistanceUnit(distanceUnit: DistanceUnit) {
    localStorage.setItem('distanceUnit', distanceUnit);
  }

  public getDistanceUnit() {
    return localStorage.getItem('distanceUnit') as DistanceUnit;
  }

  public getTimeFormat() {
    return localStorage.getItem('timeFormat');
  }

  public setDateFormat(dateFormat) {
    localStorage.setItem('dateFormat', dateFormat);
  }

  public getDateFormat() {
    return localStorage.getItem('dateFormat');
  }

  public setOnTimeRateCoefficient(onTimeRateCoefficient): void {
    localStorage.setItem('onTimeRateCoefficient', onTimeRateCoefficient);
  }

  public setPODUploadRateCoefficient(pODUploadRateCoefficient): void {
    localStorage.setItem('pODUploadRateCoefficient', pODUploadRateCoefficient);
  }

  public setMobileAppRateCoefficient(mobileAppRateCoefficient): void {
    localStorage.setItem('mobileAppRateCoefficient', mobileAppRateCoefficient);
  }

  public getOnTimeRateCoefficient(): number {
    return parseFloat(localStorage.getItem('onTimeRateCoefficient'));
  }

  public getPODUploadRateCoefficient(): number {
    return parseFloat(localStorage.getItem('pODUploadRateCoefficient'));
  }

  public getMobileAppRateCoefficient(): number {
    return parseFloat(localStorage.getItem('mobileAppRateCoefficient'));
  }

  public async setTenantTokenAsAdmin(tenantId: string): Promise<{ accessToken: string; refreshToken: string }> {
    const clientGuid = this.getClientGuid();
    const result = await this.http
      .post<{ accessToken: string; refreshToken: string }>(`${environment.serverUrl}/auth/setTenantTokenAsAdmin`, {
        tenantId,
        clientGuid,
      })
      .toPromise();
    return result;
  }

  public async fulfillInvitation(invitationCode: string): Promise<void> {
    const result = await this.http
      .post<any>(`${environment.serverUrl}/api/auth/temp-codes/${invitationCode}/fulfill`, null)
      .toPromise();
    if (result?.redirect?.url) window.location.href = result.redirect.url;
  }

  public backupAdminStoredInfo() {
    const adminToken = this.getToken();
    const adminTenant = JSON.stringify(this.getTenant());
    const adminUser = JSON.stringify(this.getUser());
    localStorage.setItem('adminToken', adminToken);
    localStorage.setItem('adminTenant', adminTenant);
    localStorage.setItem('adminUser', adminUser);
  }
  public revertAdminStorageInfo() {
    const adminTenant = JSON.parse(localStorage.getItem('adminTenant'));
    const adminUser = JSON.parse(localStorage.getItem('adminUser'));
    const adminToken = localStorage.getItem('adminToken');

    this.setToken(adminToken);
    this.setTenant(adminTenant);
    this.setUser(adminUser);
    this.removeTenantInfoInZeusView();
    localStorage.removeItem('adminTenant');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('adminToken');
  }
  async logInAsTenant(tenantId: string) {
    let token = '';
    let refreshToken = '';
    let tenant: Tenant;

    if (token === '') {
      const response: { accessToken: string; refreshToken: string } = await this.setTenantTokenAsAdmin(tenantId);
      token = response.accessToken;
      refreshToken = response.refreshToken;
      tenant = this.jwtService.decode(response.accessToken).tenant;
      this.setRefreshToken(refreshToken);
      this.setTenantInfoInZeusView(tenant);
    }

    this.backupAdminStoredInfo();

    this.setToken(token);
    this.setRefreshToken(refreshToken);
  }
  public getAdminTenant(): Tenant {
    return JSON.parse(localStorage.getItem('adminTenant'));
  }
  public getAdminUser(): User {
    return JSON.parse(localStorage.getItem('adminUser'));
  }

  public isSuperAdmin(): boolean {
    return this.getTenant()?.isAdmin || (!!this.getAdminTenant() && !!this.getAdminUser());
  }

  public isSuperAdminInZeusView(): boolean {
    return this.isSuperAdmin() && !this.getTenant().isAdmin;
  }

  public getPermissions(): string[] {
    return this.getDecodedToken().v2?.permissions || [];
  }

  async deleteCompanyAccount(): Promise<{ success: boolean }> {
    const tenant = this.getTenant();
    return await this.http
      .delete<{ success: boolean }>(`${environment.serverUrl}/auth/company-account/${tenant.id}`)
      .toPromise();
  }

  isTenantActive() {
    return !!this.getTenant().statusTag.find((tag) => tag.type === statusTag.active);
  }

  isHaulierTenantVerified() {
    const brokerHauliers = this.getBrokerHaulier();
    let status =
      brokerHauliers === undefined ? false : brokerHauliers.statusTag.some((tag) => tag.type === statusTag.active);
    return status;
  }

  public setTenantInfoInZeusView(tenant: Tenant): any {
    this.tenantInZeusView.next(tenant);
  }

  public removeTenantInfoInZeusView(): void {
    this.tenantInZeusView.next(null);
  }
}
